import React, { useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  format,
  parseISO,
} from 'date-fns';
import { Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { EVENT_TYPE_CONFIG } from './eventConstants';

/**
 * MonthView — a full calendar grid for one month.
 *
 * Each day cell shows up to 3 event pills + a "+N more" overflow.
 * Clicking a day (in write mode) triggers creation; clicking an event
 * opens its detail sheet.
 */
export default function MonthView({ events, currentMonth, onEventClick, onDayClick, isReadOnly }) {
  /* ── Build the grid (always 6 rows × 7 cols for consistent height) ── */
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentMonth]);

  /* ── Group events by day (YYYY-MM-DD key) ──────────────────────── */
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const start = typeof ev.start_at === 'string' ? parseISO(ev.start_at) : new Date(ev.start_at);
      const end = typeof ev.end_at === 'string' ? parseISO(ev.end_at) : new Date(ev.end_at);
      // For multi-day events, add to each day in the range (capped to grid)
      const dayStart = start < days[0] ? days[0] : start;
      const dayEnd = end > days[days.length - 1] ? days[days.length - 1] : end;
      const spanDays = eachDayOfInterval({ start: dayStart, end: dayEnd });
      for (const d of spanDays) {
        const key = format(d, 'yyyy-MM-dd');
        if (!map[key]) map[key] = [];
        map[key].push(ev);
      }
    }
    return map;
  }, [events, days]);

  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b bg-muted/30">
        {weekdays.map((d) => (
          <div
            key={d}
            className="py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {days.map((day, idx) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayEvents = eventsByDay[key] || [];
          const inMonth = isSameMonth(day, currentMonth);
          const today = isToday(day);
          const MAX_VISIBLE = 3;
          const visible = dayEvents.slice(0, MAX_VISIBLE);
          const overflow = dayEvents.length - MAX_VISIBLE;

          return (
            <div
              key={key}
              className={cn(
                'relative min-h-[100px] md:min-h-[120px] border-b border-r p-1.5 transition-colors group',
                !inMonth && 'bg-muted/20',
                inMonth && 'bg-card hover:bg-accent/30',
                onDayClick && inMonth && 'cursor-pointer',
              )}
              onClick={(e) => {
                // Only trigger day click if clicking the cell bg, not an event pill
                if (onDayClick && inMonth && e.target === e.currentTarget) {
                  onDayClick(day);
                }
              }}
            >
              {/* Day number */}
              <div className="flex items-center justify-between mb-1">
                <span
                  className={cn(
                    'inline-flex items-center justify-center text-xs font-medium h-6 w-6 rounded-full',
                    today && 'bg-primary text-primary-foreground',
                    !today && inMonth && 'text-foreground',
                    !today && !inMonth && 'text-muted-foreground/50',
                  )}
                >
                  {format(day, 'd')}
                </span>
                {/* + button on hover (film scope only) */}
                {onDayClick && inMonth && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDayClick(day); }}
                    className="opacity-0 group-hover:opacity-100 h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center transition-opacity"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Event pills */}
              <div className="space-y-0.5">
                {visible.map((ev) => {
                  const cfg = EVENT_TYPE_CONFIG[ev.event_type] || EVENT_TYPE_CONFIG.custom;
                  return (
                    <button
                      key={ev.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                      className={cn(
                        'w-full text-left rounded px-1.5 py-0.5 text-[11px] font-medium leading-tight truncate border transition-all',
                        'hover:ring-1 hover:ring-primary/40',
                        cfg.light,
                      )}
                      title={ev.title}
                    >
                      <span className={cn('inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle', cfg.dot)} />
                      {ev.title}
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Show first hidden event detail
                      onEventClick(dayEvents[MAX_VISIBLE]);
                    }}
                    className="w-full text-[10px] text-muted-foreground font-medium text-left pl-1.5 hover:text-primary transition-colors"
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
